import React, { useEffect, useMemo, useState } from 'react';

import { sortBy as _sortBy } from 'lodash';
import PropTypes from 'prop-types';

import {
  AsyncSingleSelect,
  Form,
  Grid,
  Input,
  PhoneNumberInput,
  SingleSelect,
  useNotification,
} from '@sine/sine-ui-components';

import mapToDropdownOptions, { mapToDropdownOptionsWithVtRequirements } from '../../../helpers/mapToDropdownOptions';
import parseServerError from '../../../helpers/parseServerError';
import notificationService from '../../../notifications/notificationService';
import companyService from '../../../services/company/company.service';
import zonesService from '../../../services/zones/zones.service';
import { useJoiForm } from '../../../shared/customHooks';
import { checkDetails } from '../helpers/visitorTypeDetailsRequired';
import generateVisitorDetailsSchema from '../schemas/personalInfoSchema';

export default function VisitorDetailsForm({
  formRef,
  isLoading,
  siteRequirements,
  pass,
  siteOptions,
  onSiteChange,
  onVisitorTypeChange,
  checkInAnother,
  editVisitorDetailsForm,
  firstNameInput,
}) {
  const [isLoadingCompanies] = useState(false);

  const companyRequired =
    siteRequirements?.companyRequired &&
    !siteRequirements?.visitorTypesExcludedFromCompanyRequired.includes(formRef.current?.values.visitorType?.value);

  const visitorTypesDetailsRequired =
    siteRequirements?.contactDetailsRequiredForSignIn &&
    siteRequirements?.detailsRequiredForSignIn?.find(
      (details) => details.visitorTypeId === formRef.current?.values.visitorType?.value,
    )?.method;

  const visitorTypeOptions = useMemo(
    () => mapToDropdownOptionsWithVtRequirements(siteRequirements?.visitorTypes),
    [siteRequirements?.visitorTypes],
  );

  const { schema } = useMemo(() => {
    const visitorSchema = generateVisitorDetailsSchema({
      ...siteRequirements,
      emailOrMobileRequired: siteRequirements?.detailsRequiredForSignIn
        ? siteRequirements?.contactDetailsRequiredForSignIn && visitorTypesDetailsRequired === 'any'
        : siteRequirements?.emailOrMobileRequired,
      companyRequired,
      visitorTypesDetailsRequired,
      detailsRequiredForSignIn: siteRequirements?.detailsRequiredForSignIn,
    });

    return { schema: visitorSchema };
  }, [companyRequired, siteRequirements, visitorTypesDetailsRequired]);

  formRef.current = useJoiForm(schema, {
    firstName: formRef.current?.values.firstName || pass.firstName || '',
    lastName: formRef.current?.values.lastName || pass.lastName || '',
    email: formRef.current?.values.email || pass.email || '',
    mobile: formRef.current?.values.mobile || pass.mobile || '',
    company: formRef.current?.values.company || pass?.company || '',
    companyId: formRef.current?.values.companyId || pass?.companyId || null,
    companySource: formRef.current?.values.companySource || pass?.companySource || null,
    site:
      formRef.current?.values.site || siteOptions.find((site) => site.value === pass?.site) || siteOptions[0] || null,
    visitorType: formRef.current?.values.visitorType || pass.visitorType || null,
    host: formRef.current?.values.host || null,
    zone: formRef.current?.values.zone || null,
  });

  const visitorDetailsForm = formRef.current;
  const currentCompanyOption = {
    label: visitorDetailsForm.values.company,
    value: visitorDetailsForm.values.companyId || visitorDetailsForm.values.company,
    sources: visitorDetailsForm.values.companySource,
  };

  const { addNotification } = useNotification();

  const loadCompanyOptions = async (searchTerm) => {
    if (searchTerm.length > 2) {
      try {
        const { verifiedCompanies = [] } = await companyService.searchScoped(searchTerm);
        return verifiedCompanies.map((company) => ({
          label: company.name,
          value: company.id,
          source: 'verified',
        }));
      } catch (error) {
        addNotification({
          message: parseServerError(error),
          variant: 'danger',
        });
      }
    }
    // return empty array if search term is less than 2 characters
    return [];
  };

  const selectedVisitorTypeOption = visitorDetailsForm.values.visitorType;
  const selectedSiteOption = visitorDetailsForm.values.site;
  const requiredHosts = siteRequirements?.hostByVisitorTypeRequired;
  const hostGroupExclusions = siteRequirements?.hostGroupVisitorTypeExclusions;

  const showHost = !!(selectedVisitorTypeOption && requiredHosts?.includes(selectedVisitorTypeOption.value));
  const zonesEnabled = siteRequirements?.zonesEnabled && !!siteRequirements?.defaultZoneId;
  const [zoneOptions, setZoneOptions] = useState({ values: [], defaultOption: null });

  // HGxVT
  const { activeHostGroupIdExclusions, activeHostExclusions } = useMemo(() => {
    const _activeHostGroupIdExclusions = (hostGroupExclusions || [])
      .filter((exclusion) => exclusion.visitorTypeId === selectedVisitorTypeOption?.value)
      .map((exclusion) => exclusion.hostGroupId);

    const _activeHostExclusions = (siteRequirements?.hostGroups || [])
      .filter((hostGroup) => _activeHostGroupIdExclusions.includes(hostGroup?.id))
      .flatMap((hostGroup) => hostGroup.hosts.map((host) => host.id));

    return { activeHostGroupIdExclusions: _activeHostGroupIdExclusions, activeHostExclusions: _activeHostExclusions };
  }, [hostGroupExclusions, selectedVisitorTypeOption?.value, siteRequirements?.hostGroups]);

  const hostOptions = useMemo(() => {
    const hostsList = (siteRequirements?.hostGroups || [])
      .filter((hostGroup) => !activeHostGroupIdExclusions.includes(hostGroup.id))
      .flatMap((hostGroup) =>
        hostGroup.hosts.map((host) => ({
          label: `${host.firstName} ${host.lastName} (${hostGroup.name})`,
          value: host.id,
        })),
      );

    return _sortBy(hostsList, 'label');
  }, [activeHostGroupIdExclusions, siteRequirements?.hostGroups]);

  useEffect(() => {
    // reset validation errors when visitor type changes
    visitorDetailsForm.resetErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, selectedVisitorTypeOption]);

  useEffect(() => {
    const visitorTypeOnPass = visitorTypeOptions.find((visitorType) => visitorType.value === pass.visitorType);

    if (pass?.site === selectedSiteOption?.value) {
      visitorDetailsForm.onChange('visitorType', visitorTypeOnPass || null);
    } else {
      visitorDetailsForm.onChange('visitorType', visitorTypeOptions[0] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass?.site, pass.visitorType, selectedSiteOption?.value, visitorTypeOptions]);

  useEffect(() => {
    const hostOnPass = hostOptions.find((host) => host.value === pass.host);

    if (!showHost) {
      visitorDetailsForm.onChange('host', null);
      visitorDetailsForm.onChange('zone', null);
    }

    if (pass?.host) {
      visitorDetailsForm.onChange('host', (showHost && hostOnPass) || hostOptions[0]);
    } else if (!visitorDetailsForm.values.host) {
      visitorDetailsForm.onChange('host', (showHost && hostOptions[0]) || null);
    }
  }, [showHost]);

  useEffect(() => {
    onVisitorTypeChange(selectedVisitorTypeOption);

    const selectedHostOption = visitorDetailsForm?.values?.host?.value;
    const noHostSelected = showHost && !selectedHostOption;

    const shouldResetSelectedHost = activeHostExclusions.includes(selectedHostOption) || noHostSelected;
    if (shouldResetSelectedHost) {
      const hostOnPass = hostOptions.find((host) => host.value === pass.host);
      visitorDetailsForm.onChange('host', hostOnPass || hostOptions[0] || null);
    }
  }, [selectedVisitorTypeOption]);

  useEffect(() => {
    onSiteChange(selectedSiteOption);
  }, [selectedSiteOption]);

  useEffect(() => {
    if (editVisitorDetailsForm || !checkInAnother) {
      visitorDetailsForm.onChange('site', formRef.current?.values.site || siteOptions[0] || null);
      visitorDetailsForm.onChange('visitorType', formRef.current?.values.visitorType || null);
      visitorDetailsForm.onChange('host', (showHost && formRef.current?.values.host) || null);
    }

    firstNameInput.current.focus();
  }, []);

  useEffect(() => {
    if (
      (siteRequirements?.emailOrMobileRequired ||
        (siteRequirements?.contactDetailsRequiredForSignIn &&
          checkDetails(siteRequirements?.detailsRequiredForSignIn, selectedVisitorTypeOption, 'any'))) &&
      visitorDetailsForm.errors.mobile === undefined &&
      visitorDetailsForm.errors.email?.includes('Email or mobile required') &&
      visitorDetailsForm.errors.email?.length === 1
    ) {
      visitorDetailsForm.onError('email', '');
    }
  }, [visitorDetailsForm.values.mobile]);

  useEffect(() => {
    if (
      (siteRequirements?.emailOrMobileRequired ||
        (siteRequirements?.contactDetailsRequiredForSignIn &&
          checkDetails(siteRequirements?.detailsRequiredForSignIn, selectedVisitorTypeOption, 'any'))) &&
      visitorDetailsForm.errors.email === undefined &&
      visitorDetailsForm.errors.mobile?.includes('Email or mobile required') &&
      visitorDetailsForm.errors.mobile?.length === 1
    ) {
      visitorDetailsForm.onError('mobile', '');
    }
  }, [visitorDetailsForm.values.email]);

  useEffect(() => {
    if (visitorDetailsForm.values.host && visitorDetailsForm.values.site && zonesEnabled) {
      (async () => {
        try {
          const zonesForHostProfile = await zonesService.getZonesForHostProfile(
            visitorDetailsForm.values.site.value,
            visitorDetailsForm.values.host.value,
          );

          if (!zonesForHostProfile.length) {
            setZoneOptions({ values: [], defaultOption: null });
            return;
          }

          const invitationZone = zonesForHostProfile.find((zone) => zone.id === pass.zone);
          const defaultZoneForHostGroup = zonesForHostProfile.find((zone) =>
            zone.hostGroups?.find((zoneHostGroup) => zoneHostGroup.default),
          );
          const defaultZoneForSite = zonesForHostProfile.find((zone) => zone.default);

          const defaultZone = invitationZone || defaultZoneForHostGroup || defaultZoneForSite;

          setZoneOptions({
            values: mapToDropdownOptions(zonesForHostProfile),
            defaultOption: { label: defaultZone.name, value: defaultZone.id },
          });
        } catch (error) {
          notificationService.addNotification({
            message: parseServerError(error),
            variant: 'danger',
          });
        }
      })();
    } else {
      setZoneOptions({ values: [], defaultOption: null });
    }
  }, [visitorDetailsForm.values.host]);

  useEffect(() => {
    visitorDetailsForm.onChange('zone', zoneOptions.values.length ? zoneOptions.defaultOption : null);
  }, [zoneOptions.values]);

  const handleFormChange = (name, value) => {
    if (name === 'company' && value) {
      visitorDetailsForm.onChange('company', value.label);
      visitorDetailsForm.onChange('companyId', value.label !== value.value ? value.value : null);
      visitorDetailsForm.onChange('companySource', value.source || 'new');
    } else {
      visitorDetailsForm.onChange(name, value);
    }
  };

  return (
    <Form
      formSpacing='compact'
      values={visitorDetailsForm.values}
      errors={!isLoading ? visitorDetailsForm.errors : undefined}
      onFieldChange={handleFormChange}
      onFieldBlur={visitorDetailsForm.onBlur}
    >
      <Grid container>
        <Grid sm={6} alignItems='stretch'>
          <Input name='firstName' label='First Name' data-st-ext='input-first-name' ref={firstNameInput} />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <Input name='lastName' label='Last Name' data-st-ext='input-last-name' />
        </Grid>

        <Grid sm={12} alignItems='stretch'>
          {!isLoadingCompanies && (
            <AsyncSingleSelect
              data-st-ext='select-companies'
              label='Company'
              name='company'
              value={currentCompanyOption}
              searchFunction={loadCompanyOptions}
              optional={!companyRequired}
              allowFreetext
              onSearchError={() => {}}
              initialOptions={visitorDetailsForm.values.company ? [currentCompanyOption] : undefined}
            />
          )}
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <Input
            name='email'
            label='Email'
            data-st-ext='input-email'
            optional={
              siteRequirements?.detailsRequiredForSignIn
                ? !siteRequirements.contactDetailsRequiredForSignIn ||
                  checkDetails(siteRequirements.detailsRequiredForSignIn, selectedVisitorTypeOption, ['mobile', 'none'])
                : !siteRequirements?.emailOrMobileRequired && !siteRequirements?.emailRequired
            }
          />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          {!isLoading && (
            <PhoneNumberInput
              name='mobile'
              label='Mobile'
              defaultCountryCode={selectedSiteOption?.countryCode}
              data-st-ext='input-mobile'
              optional={
                siteRequirements?.detailsRequiredForSignIn
                  ? !siteRequirements.contactDetailsRequiredForSignIn ||
                    checkDetails(siteRequirements.detailsRequiredForSignIn, selectedVisitorTypeOption, [
                      'email',
                      'none',
                    ])
                  : !siteRequirements?.emailOrMobileRequired && !siteRequirements?.mobileRequired
              }
            />
          )}
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <SingleSelect name='site' label='Site' options={siteOptions} data-st-ext='select-site' />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <SingleSelect
            name='visitorType'
            label='Visitor Type'
            options={visitorTypeOptions}
            data-st-ext='select-visitor-type'
            disableSearch
          />
        </Grid>

        {showHost && (
          <Grid sm={zoneOptions.values.length ? 6 : 12} alignItems='stretch'>
            <SingleSelect name='host' label='Host' options={hostOptions} data-st-ext='select-host' />
          </Grid>
        )}

        {showHost && zonesEnabled && !!zoneOptions.values.length && (
          <Grid sm={6} alignItems='stretch'>
            <SingleSelect name='zone' label='Zone' options={zoneOptions.values} data-st-ext='select-zone' />
          </Grid>
        )}
      </Grid>
    </Form>
  );
}

VisitorDetailsForm.propTypes = {
  formRef: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  siteRequirements: PropTypes.object.isRequired,
  pass: PropTypes.object.isRequired,
  siteOptions: PropTypes.array.isRequired,
  onSiteChange: PropTypes.func.isRequired,
  onVisitorTypeChange: PropTypes.func.isRequired,
  editVisitorDetailsForm: PropTypes.bool,
  checkInAnother: PropTypes.bool,
  firstNameInput: PropTypes.object,
};
